import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
} from 'react';

const LoadingContext = createContext({
  isLoading: false,
  showLoading: () => {},
  hideLoading: () => {},
  beginExpectedTasks: (ids) => {},
  finishTask: (id) => {},
  progress: 0,
});

export const LoadingProvider = ({ children }) => {
  const counter = useRef(0);
  const remainingTasks = useRef(new Set());
  const rafScheduled = useRef(false);

  const [, forceUpdate] = useState(0);

  const scheduleUpdate = useCallback(() => {
    if (!rafScheduled.current) {
      rafScheduled.current = true;
      requestAnimationFrame(() => {
        rafScheduled.current = false;
        forceUpdate((n) => n + 1);
      });
    }
  }, []);

  const showLoading = useCallback(() => {
    counter.current += 1;
    scheduleUpdate();
  }, [scheduleUpdate]);

  const hideLoading = useCallback(() => {
    counter.current = Math.max(0, counter.current - 1);
    scheduleUpdate();
  }, [scheduleUpdate]);

  const beginExpectedTasks = useCallback(
    (ids = []) => {
      remainingTasks.current = new Set(ids.filter(Boolean));
      scheduleUpdate();
    },
    [scheduleUpdate]
  );

  const finishTask = useCallback(
    (id) => {
      if (!id) return;
      if (remainingTasks.current.has(id)) {
        remainingTasks.current.delete(id);
        scheduleUpdate();
      }
    },
    [scheduleUpdate]
  );

  const isLoading = counter.current > 0 || remainingTasks.current.size > 0;

  const progress = (() => {
    const totalExpectedTasks = Math.max(1, remainingTasks.current.size);
    const completedTasks = Math.max(
      0,
      totalExpectedTasks - remainingTasks.current.size
    );
    return Math.round((completedTasks / totalExpectedTasks) * 100);
  })();

  const value = {
    isLoading,
    showLoading,
    hideLoading,
    beginExpectedTasks,
    finishTask,
    progress,
  };

  return (
    <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within LoadingProvider');
  }
  return context;
};
